import React, { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import { getUserViewSettings } from "redux/actions/userSettingsActions";
import "./styles.css";

const ViewListDrawer = ({
  open,
  onClose,
  tableName = "",
  userId = null,
  clientId = null,
  currentUserEmail = "",
  selectedViewId = null,
  onSelectView,
  onSetDefault,
  onRenameView,
  onShareToggle,
  onDeleteView,
  actionLoadingId = null,
  isTransactionPage = false,
}) => {
  const dispatch = useDispatch();
  
  // Get views from Redux state
  const views = useSelector((state) => 
    Array.isArray(state.userSettings.userViewSettings) 
      ? state.userSettings.userViewSettings 
      : []
  );

  // Fetch user view settings whenever the drawer opens
  useEffect(() => {
    if (open && tableName && userId && clientId) {
      dispatch(getUserViewSettings(tableName, userId, clientId));
    }
  }, [open, tableName, userId, clientId, dispatch]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuView, setMenuView] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingViewId, setEditingViewId] = useState(null);
  const [editingViewName, setEditingViewName] = useState("");
  const [originalViewName, setOriginalViewName] = useState("");
  const [nameError, setNameError] = useState("");
  const [hoveredViewId, setHoveredViewId] = useState(null);

  useEffect(() => {
    if (!open) {
      setMenuAnchor(null);
      setMenuView(null);
      setSearchQuery("");
      setEditingViewId(null);
      setEditingViewName("");
      setOriginalViewName("");
      setNameError("");
      setHoveredViewId(null);
    }
  }, [open]);

  const { privateViews, publicViews } = useMemo(() => {
    // Filter views by search query
    const filteredViews = views.filter((v) =>
      v.viewName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // isPrivate: true = private view, false = public/shared view
    const privateList = filteredViews.filter((v) => v.isPrivate === true);
    const publicList = filteredViews.filter((v) => v.isPrivate === false);

    // Sort each list: default view first, then alphabetically
    const sortViews = (viewList) => {
      return viewList.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.viewName.localeCompare(b.viewName);
      });
    };

    return { 
      privateViews: sortViews(privateList), 
      publicViews: sortViews(publicList) 
    };
  }, [views, searchQuery]);

  const defaultOrFirstViewId = useMemo(() => {
    // Only return a view ID if there's a default view
    const defaultView = views.find((v) => v.isDefault);
    if (defaultView) return defaultView.id;
    // Don't automatically select the first view - return null
    return null;
  }, [views]);

  const activeViewId = selectedViewId || defaultOrFirstViewId;

  const handleMenuOpen = (event, view) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuView(view);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuView(null);
  };

  const handleMenuAction = (action) => {
    if (!menuView) return;
    switch (action) {
      case "setDefault":
        onSetDefault?.(menuView);
        break;
      case "rename":
        // Enter edit mode
        setEditingViewId(menuView.id);
        setEditingViewName(menuView.viewName);
        setOriginalViewName(menuView.viewName);
        setNameError("");
        break;
      case "share":
        onShareToggle?.(menuView, "share");
        break;
      case "makePrivate":
        onShareToggle?.(menuView, "makePrivate");
        break;
      case "restrict":
      case "unrestrict":
        // You may need to add a new callback prop for this action
        // For now, calling onShareToggle with the action type
        onShareToggle?.(menuView, action);
        break;
      case "delete":
        onDeleteView?.(menuView);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const handleEditNameChange = (e) => {
    const newName = e.target.value;
    setEditingViewName(newName);
    
    // Check if name already exists (excluding current view)
    const nameExists = views.some(
      (v) => v.id !== editingViewId && v.viewName.trim().toLowerCase() === newName.trim().toLowerCase() && v.isOwner === true
    );
    
    if (nameExists) {
      setNameError("This label already exists.");
    } else if (!newName.trim()) {
      setNameError("Name cannot be empty.");
    } else {
      setNameError("");
    }
  };

  const handleSaveRename = () => {
    if (nameError || !editingViewName.trim()) return;
    
    const viewToRename = views.find((v) => v.id === editingViewId);
    if (viewToRename) {
      onRenameView?.({ ...viewToRename, viewName: editingViewName.trim() });
    }
    
    setEditingViewId(null);
    setEditingViewName("");
    setOriginalViewName("");
    setNameError("");
  };

  const handleCancelRename = () => {
    setEditingViewId(null);
    setEditingViewName("");
    setOriginalViewName("");
    setNameError("");
  };

  const handleClearInput = () => {
    setEditingViewName("");
    setNameError("Name cannot be empty.");
  };

  const handleRowClick = (view) => {
    onSelectView?.(view);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatCreatedBy = (view) => {
    if (!view.createdBy) return '';
    // Check if current user is the creator
    if (userId && view.userId && userId === view.userId) {
      return 'me';
    }
    return view.userName ? view.userName.replace('.', ' ') : '';
  };

  const renderViewTooltip = (view) => {
    if (view.isPrivate) return null;
    
    return (
      <Box className="view-tooltip-container">
        <Typography className="view-tooltip-title">
         Account summaries
        </Typography>
        {view.createdBy && (
          <Typography className="view-tooltip-item">
            <span className="view-tooltip-bullet">•</span> Created by {formatCreatedBy(view)}
          </Typography>
        )}
        {view.createdDate && (
          <Typography className="view-tooltip-item">
            <span className="view-tooltip-bullet">•</span> Created on {formatDate(view.createdDate)}
          </Typography>
        )}
        {view.modifiedBy && (
          <Typography className="view-tooltip-item">
            <span className="view-tooltip-bullet">•</span> Last modified by {view.modifiedBy}
          </Typography>
        )}
        {view.modifiedDate && (
          <Typography className="view-tooltip-item view-tooltip-item-last">
            <span className="view-tooltip-bullet">•</span> Last modified on {formatDate(view.modifiedDate)}
          </Typography>
        )}
      </Box>
    );
  };

  const renderViewItem = (view) => {
    const isOwner = view.isOwner;
    const isSelected = activeViewId === view.id;
    const loading = actionLoadingId === view.id;
    const isEditing = editingViewId === view.id;
    const isAnyEditing = editingViewId !== null;
    const isDisabled = isAnyEditing && !isEditing;
    const isHovered = hoveredViewId === view.id;

    // Determine if menu should be shown based on available actions
    const hasMenuActions = 
      view.isOwner || // Owner has all actions
      !view.isDefault; // Non-owners can only interact with non-default views (to set as default)

    const itemClasses = [
      "view-list-item",
      isEditing && "view-list-item-editing",
      isDisabled && "view-list-item-disabled"
    ].filter(Boolean).join(" ");

    return (
      <ListItem
        key={view.id}
        className={itemClasses}
        disablePadding
        onClick={() => !isEditing && !isDisabled && handleRowClick(view)}
        onMouseEnter={() => setHoveredViewId(view.id)}
        onMouseLeave={() => setHoveredViewId(null)}
        selected={isSelected}
      >
        <Radio
          checked={isSelected}
          onChange={() => handleRowClick(view)}
          value={String(view.id)}
          name="view-selection"
          color="primary"
          size="small"
          className="view-list-item-radio"
          disabled={isEditing || isDisabled}
        />
        {isEditing ? (
          <Box className="view-list-item-edit-container">
            <TextField
              value={editingViewName}
              onChange={handleEditNameChange}
              size="small"
              autoFocus
              error={!!nameError}
              className="view-list-item-edit-input"
              onClick={(e) => e.stopPropagation()}
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: editingViewName && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearInput();
                      }}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveRename();
              }}
              disabled={
                !!nameError || 
                !editingViewName.trim() || 
                editingViewName.trim() === originalViewName.trim()
              }
              className="view-list-item-edit-save"
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelRename();
              }}
              className="view-list-item-edit-cancel"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : !view.isPrivate ? (
          <CustomTooltip
            title={renderViewTooltip(view)}
            arrow
            placement="top"
            enterDelay={300}
            leaveDelay={200}
            maxWidth="280px"
          >
            <Box className="view-list-item-content">
              <Box className="view-list-item-row">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                  <Typography className="view-list-item-name">
                    {view.viewName}
                  </Typography>
                  {view.isDefault && (
                    <Typography className="view-list-item-default-badge">
                      Default
                    </Typography>
                  )}
                </Box>
                {hasMenuActions && (
                  <IconButton
                    className={`view-list-item-menu-button ${isHovered ? 'view-list-item-menu-button-visible' : ''}`}
                    size="small"
                    onClick={(e) => handleMenuOpen(e, view)}
                    onMouseEnter={(e) => e.stopPropagation()}
                    onMouseLeave={(e) => e.stopPropagation()}
                    aria-label="View actions"
                    aria-haspopup="true"
                    aria-controls={menuAnchor ? "view-actions-menu" : undefined}
                    disabled={loading || isAnyEditing}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {view.createdBy && (
                <Typography className="view-list-item-meta">
                  Created by {formatCreatedBy(view)}
                </Typography>
              )}
            </Box>
          </CustomTooltip>
        ) : (
          <Box className="view-list-item-row">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <Typography className="view-list-item-name">
                {view.viewName}
              </Typography>
              {view.isDefault && (
                <Typography className="view-list-item-default-badge">
                  Default
                </Typography>
              )}
            </Box>
            {hasMenuActions && (
              <IconButton
                className={`view-list-item-menu-button ${isHovered ? 'view-list-item-menu-button-visible' : ''}`}
                size="small"
                onClick={(e) => handleMenuOpen(e, view)}
                aria-label="View actions"
                aria-haspopup="true"
                aria-controls={menuAnchor ? "view-actions-menu" : undefined}
                disabled={loading || isAnyEditing}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
        {isEditing && nameError && (
          <Box className="view-list-item-error">
            <WarningAmberIcon className="view-list-item-error-icon" fontSize="small" />
            <Typography className="view-list-item-error-text">
              {nameError}
            </Typography>
          </Box>
        )}
      </ListItem>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="view-list-drawer"
    >
      <Box className="view-list-drawer-header">
        <Box className="view-list-drawer-header-actions">
          <Typography variant="h6" className="view-list-drawer-header-title">
            View: {views.find(v => v.id === activeViewId)?.viewName || "Unselected"}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close" className="view-list-drawer-close-button">
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          className="view-list-drawer-search"
          placeholder="Search by view name"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className="view-list-content">
        <RadioGroup
          name="view-selection"
          value={activeViewId !== null && activeViewId !== undefined ? String(activeViewId) : ""}
          onChange={(e) => {
            const targetValue = e.target.value;
            const v = views.find((x) => String(x.id) === targetValue);
            if (v) onSelectView?.(v);
          }}
        >
          <Box className="view-list-private-section">
            <Typography className="view-list-section-title">Private</Typography>
            <Box className="view-list-section-scrollable">
              {privateViews.length === 0 ? (
                <Box className="view-list-empty">No views found</Box>
              ) : (
                <List disablePadding>
                  {privateViews.map((v) => renderViewItem(v))}
                </List>
              )}
            </Box>
          </Box>

          <Divider className="view-list-section-divider" />

          <Box className="view-list-public-section">
            <Typography className="view-list-section-title">Public</Typography>
            <Box className="view-list-section-scrollable">
              {publicViews.length === 0 ? (
                <Box className="view-list-empty">No views found</Box>
              ) : (
                <List disablePadding>
                  {publicViews.map((v) => renderViewItem(v))}
                </List>
              )}
            </Box>
          </Box>
        </RadioGroup>
      </Box>

      <Menu
        id="view-actions-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className: "view-list-menu",
        }}
        MenuListProps={{
          "aria-labelledby": "view-actions-button",
        }}
      >
        {menuView && (
          <>
            {/* Set as default - show for all non-default views */}
            {!menuView.isDefault && (
              <MenuItem onClick={() => handleMenuAction("setDefault")}>
                <ListItemText primary="Set as default" />
              </MenuItem>
            )}
            
            {/* Rename - show for owner OR for non-owner if public and unrestricted */}
            {(menuView.isOwner || menuView.isPrivate) && (
              <MenuItem onClick={() => handleMenuAction("rename")}>
                <ListItemText primary="Rename" />
              </MenuItem>
            )}
            
            {/* Owner-only options */}
            {menuView.isOwner && (
              <>
                {/* Share - only for private views */}
                {menuView.isPrivate && (
                  <MenuItem onClick={() => handleMenuAction("share")}>
                    <ListItemText primary="Share with others" />
                  </MenuItem>
                )}
                
                {/* Restrict/Unrestrict - only for public views */}
                {!menuView.isPrivate && (
                  <MenuItem 
                    onClick={() => handleMenuAction(menuView.isRestricted ? "unrestrict" : "restrict")}
                    className="view-list-menu-item-restrict"
                  >
                    <ListItemText primary={menuView.isRestricted ? "Unrestrict" : "Restrict"} />
                    <CustomTooltip
                      title={menuView.isRestricted 
                        ? "If unrestricted, anyone can modify this view." 
                        : "If restricted, only you can modify this view."
                      }
                      arrow
                      placement="top"
                      enterDelay={300}
                      leaveDelay={200}
                      maxWidth="174px"
                    >
                      <InfoOutlinedIcon 
                        className="view-list-menu-item-info-icon"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </CustomTooltip>
                  </MenuItem>
                )}
                
                {/* Make Private - only for public views */}
                {!menuView.isPrivate && (
                  <MenuItem onClick={() => handleMenuAction("makePrivate")}>
                    <ListItemText primary="Make it private" />
                  </MenuItem>
                )}
                
                {/* Delete - always show for owner */}
                <MenuItem onClick={() => handleMenuAction("delete")}>
                  <ListItemText primary={menuView.isPrivate ? "Delete" : "Delete for everyone"} />
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>
    </Drawer>
  );
};

export default React.memo(ViewListDrawer);
